import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import styles from './ComparisonOverview.module.css';
import PDFService from '../../../services/pdfService';

/**
 * ComparisonOverview component that handles both full detailed analysis data (for paid users)
 * and basic guest analysis data (for free tier users migrated from guest)
 * 
 * Props:
 * - recommendationData, evaluationData, consolidatedData: Full analysis format (paid users)
 * - guestAnalysisData: Basic analysis from guest_comparision_analysis_report.json (free users)
 * - userPlan: 'free', 'essential', 'complete', 'expert'
 * - hasActiveSubscription: boolean
 */
const ComparisonOverview = ({ 
  recommendationData, 
  evaluationData, 
  consolidatedData,
  guestAnalysisData, // New prop for guest analysis format
  onUpgradeClick,
  userPlan = 'free',
  hasActiveSubscription = false
}) => {
  // State for expanded missing elements
  const [expandedMissingElements, setExpandedMissingElements] = useState({});

  // Toggle function for missing elements expansion
  const toggleMissingElements = (contractorName) => {
    setExpandedMissingElements(prev => ({
      ...prev,
      [contractorName]: !prev[contractorName]
    }));
  };
  
  // Debug: Log all incoming data
  console.log('ComparisonOverview - Incoming data:', {
    recommendationData,
    evaluationData,
    consolidatedData,
    guestAnalysisData,
    userPlan,
    hasActiveSubscription,
    isUsingGuestData: guestAnalysisData && (!consolidatedData || userPlan === 'free')
  });
  
  // Check if we're using guest analysis data (for free tier users)
  const isUsingGuestData = guestAnalysisData && (!consolidatedData || userPlan === 'free');
  
  // Transform guest data to expected format for free tier users
  const transformGuestData = () => {
    if (!guestAnalysisData) {
      return { contractors: [], recommendation: null };
    }
    
    // Handle nested structure from API response (data.guestAnalysis)
    const actualData = guestAnalysisData.guestAnalysis || guestAnalysisData;
    
    const contractors = [];
    
    // Transform Contractor_1_Information
    if (actualData.Contractor_1_Information) {
      const c1 = actualData.Contractor_1_Information;
      contractors.push({
        contractor: {
          contractor_name: c1.Company_Name || 'Contractor 1'
        },
        payment_terms: {
          total_cost: c1.Financials?.Base_Price?.amount || c1.Financials?.total_cost || '0'
        },
        project_scope: c1.Scope?.Included_Work_Scope || c1.project_scope || 'Scope not specified',
        exclusions: c1.Scope?.Exclusions || c1.exclusions || [],
        allowances: {
          direct_costs: c1.Financials?.Cost_Breakdown?.allowances?.direct || c1.allowances?.direct_costs || {}
        }
      });
    }
    
    // Transform Contractor_2_Information
    if (actualData.Contractor_2_Information) {
      const c2 = actualData.Contractor_2_Information;
      contractors.push({
        contractor: {
          contractor_name: c2.Company_Name || 'Contractor 2'
        },
        payment_terms: {
          total_cost: c2.Financials?.Base_Price?.amount || c2.Financials?.total_cost || '0'
        },
        project_scope: c2.Scope?.Included_Work_Scope || c2.project_scope || 'Scope not specified',
        exclusions: c2.Scope?.Exclusions || c2.exclusions || [],
        allowances: {
          direct_costs: c2.Financials?.Cost_Breakdown?.allowances?.direct || c2.allowances?.direct_costs || {}
        }
      });
    }
    
    // Transform recommendation data
    const recommendation = actualData.comparision?.Recommendation ? {
      recommended_contractor: actualData.comparision.Recommendation.Preferred_Contractor,
      reasoning: actualData.comparision.Recommendation.Reason,
      ranking: [
        {
          contractor_name: contractors[0]?.contractor?.contractor_name || 'Contractor 1',
          alignment_score: actualData.comparision?.Contractor_1_Score?.Total || 0
        },
        {
          contractor_name: contractors[1]?.contractor?.contractor_name || 'Contractor 2', 
          alignment_score: actualData.comparision?.Contractor_2_Score?.Total || 0
        }
      ]
    } : null;
    
    return { contractors, recommendation };
  };
  
  // State for price comparison mode
  const [comparisonMode, setComparisonMode] = useState('average'); // 'average', 'highest', 'lowest'
  
  // State for allowances view mode
  const [allowancesViewMode, setAllowancesViewMode] = useState('chart'); // 'chart', 'table'
  
  // State for PDF generation
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // PDF Download Handler with fallback
  const handleDownloadPDF = async () => {
    if (isGeneratingPDF) return;
    
    try {
      setIsGeneratingPDF(true);
      
      const reportData = {
        recommendationData,
        evaluationData,
        consolidatedData,
        guestAnalysisData,
        userPlan,
        hasActiveSubscription
      };
      
      console.log('Attempting primary PDF generation...');
      
      try {
        // Try primary PDF generation first
        await PDFService.generateAndDownloadPDF(reportData);
      } catch (primaryError) {
        console.warn('Primary PDF generation failed, trying fallback:', primaryError);
        
        // If primary fails, try HTML-to-PDF fallback
        await PDFService.generateHTMLToPDF(reportData);
      }
      
    } catch (error) {
      console.error('All PDF generation methods failed:', error);
      
      // Show user-friendly error message
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: white;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 30px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
        z-index: 10000;
        font-family: Inter, sans-serif;
        max-width: 400px;
        text-align: center;
      `;
      errorDiv.innerHTML = `
        <div style="color: #ef4444; font-size: 48px; margin-bottom: 15px;">⚠️</div>
        <h3 style="color: #1e293b; margin-bottom: 10px; font-size: 18px;">PDF Generation Failed</h3>
        <p style="color: #64748b; margin-bottom: 20px; line-height: 1.5;">
          Unable to generate PDF report. This might be due to browser compatibility issues or missing dependencies.
        </p>
        <button onclick="this.parentElement.remove()" 
          style="background: #0077cc; color: white; border: none; padding: 12px 24px; border-radius: 6px; font-weight: 600; cursor: pointer;">
          Close
        </button>
      `;
      document.body.appendChild(errorDiv);
    } finally {
      setIsGeneratingPDF(false);
    }
  };
  
  // State for expandable scoring explanation
  const [expandedScoringExplanation, setExpandedScoringExplanation] = useState(false);
  
  // Helper functions to determine feature access based on plan
  const canAccessBasicAnalysis = () => {
    // Free users get basic analysis when using guest data, Essential+ users get basic analysis
    if (isUsingGuestData && userPlan === 'free') {
      return true; // Free users get basic analysis with guest data
    }
    return hasActiveSubscription && ['essential', 'complete', 'expert'].includes(userPlan);
  };
  
  const canAccessDetailedAnalysis = () => {
    // Complete+ plans get detailed analysis (allowance breakdown, missing elements)
    return hasActiveSubscription && ['complete', 'expert'].includes(userPlan);
  };
  
  const canAccessAdvancedFeatures = () => {
    // Expert plan gets advanced features (negotiation guides, advanced risk modeling)
    return hasActiveSubscription && userPlan === 'expert';
  };
  
  const canAccessRecommendationReasoning = () => {
    // Free users get basic recommendation with guest data, Essential+ users get recommendation reasoning
    if (isUsingGuestData && userPlan === 'free') {
      return true; // Free users get basic recommendation reasoning
    }
    return hasActiveSubscription && ['essential', 'complete', 'expert'].includes(userPlan);
  };
  
  const canAccessDetailedScope = () => {
    // Complete+ plans get detailed scope comparison
    return hasActiveSubscription && ['complete', 'expert'].includes(userPlan);
  };

  const canAccessContractorScoring = () => {
    // Essential+ plans get contractor scoring and evaluation
    return hasActiveSubscription && ['essential', 'complete', 'expert'].includes(userPlan);
  };

  const canAccessCostBreakdown = () => {
    // Essential+ plans get basic cost breakdown, Complete+ gets detailed breakdown
    return hasActiveSubscription && ['essential', 'complete', 'expert'].includes(userPlan);
  };

  const canAccessRiskAnalysis = () => {
    // Essential+ plans get basic risk analysis, Complete+ gets comprehensive risk analysis
    return hasActiveSubscription && ['essential', 'complete', 'expert'].includes(userPlan);
  };
  
  // Extract contractor data - use guest data if available, otherwise use consolidated data
  const contractors = (() => {
    if (isUsingGuestData) {
      const transformedData = transformGuestData();
      return transformedData.contractors;
    }
    
    return consolidatedData ? 
      [1, 2, 3, 4, 5]
        .map(num => consolidatedData[`contractor_${num}`])
        .filter(Boolean) 
      : [];
  })();

  // Get contractor colors - expanded palette for up to 5 contractors
  const getContractorColors = () => {
    const colors = ['#0077cc', '#ff9800', '#4caf50', '#9c27b0', '#f44336'];
    const contractorColors = {};
    contractors.forEach((contractor, index) => {
      const fullName = contractor.contractor.contractor_name;
      
      // Use the same dynamic name truncation logic as prepareCostData
      let maxLength;
      if (contractors.length <= 2) {
        maxLength = 18;
      } else if (contractors.length <= 3) {
        maxLength = 12;
      } else if (contractors.length <= 4) {
        maxLength = 10;
      } else {
        maxLength = 8;
      }
      
      const displayName = fullName.length > maxLength ? fullName.substring(0, maxLength - 3) + '...' : fullName;
      
      // Map both full name and display name to same color for consistency
      contractorColors[fullName] = colors[index % colors.length];
      contractorColors[displayName] = colors[index % colors.length];
    });
    return contractorColors;
  };

  const contractorColors = getContractorColors();

  // Prepare cost comparison data
  const prepareCostData = () => {
    return contractors.map(contractor => {
      // Parse total cost
      let totalCost = 0;
      if (contractor.payment_terms && contractor.payment_terms.total_cost) {
        const costStr = contractor.payment_terms.total_cost.toString();
        const cleanCost = costStr.replace(/[\$,]/g, '');
        totalCost = parseFloat(cleanCost) || 0;
      }
      
      const fullName = contractor.contractor.contractor_name;
      
      // Adjust name length based on number of contractors
      let maxLength;
      if (contractors.length <= 2) {
        maxLength = 18;
      } else if (contractors.length <= 3) {
        maxLength = 12;
      } else if (contractors.length <= 4) {
        maxLength = 10;
      } else {
        maxLength = 8;
      }
      
      const displayName = fullName.length > maxLength ? fullName.substring(0, maxLength - 3) + '...' : fullName;
      
      return {
        name: displayName,
        fullName: fullName, // Keep full name for tooltip
        cost: totalCost
      };
    });
  };

  // Get contractor alignment scores from recommendation data
  const getAlignmentScores = () => {
    if (isUsingGuestData) {
      const transformedData = transformGuestData();
      return transformedData.recommendation?.ranking?.map(item => ({
        name: item.contractor_name,
        score: item.alignment_score
      })) || [];
    }
    
    if (!recommendationData?.ranking) return [];
    
    return recommendationData.ranking.map(item => ({
      name: item.contractor_name,
      score: item.alignment_score
    }));
  };

  // Calculate price differences - supports multiple comparison modes
  const detectOutliers = () => {
    const costData = prepareCostData();
    if (costData.length < 1) return [];
    
    const costs = costData.map(d => d.cost);
    
    // Calculate reference price based on comparison mode
    let referencePrice;
    let referenceName;
    
    switch (comparisonMode) {
      case 'highest':
        referencePrice = Math.max(...costs);
        referenceName = 'highest';
        break;
      case 'lowest':
        referencePrice = Math.min(...costs);
        referenceName = 'lowest';
        break;
      case 'average':
      default:
        referencePrice = costs.reduce((sum, cost) => sum + cost, 0) / costs.length;
        referenceName = 'average';
        break;
    }
    
    return costData.map(item => {
      // Calculate percentage difference from reference price
      const percentageDiff = ((item.cost - referencePrice) / referencePrice * 100);
      const deviation = Math.abs(percentageDiff).toFixed(1);
      const isHigher = percentageDiff > 0;
      
      // Consider it an outlier if more than 15% different from reference
      const isOutlier = Math.abs(percentageDiff) > 15;
      
      return {
        name: item.fullName || item.name, // Use full name for display
        displayName: item.name, // Keep display name for charts
        cost: item.cost,
        isOutlier,
        deviation,
        isHigher,
        percentageFromRef: percentageDiff.toFixed(1),
        referencePrice,
        referenceName
      };
    });
  };

  // Find missing elements with details
  const findMissingElements = () => {
    if (isUsingGuestData) {
      // For guest data, we can show risk alerts as "missing elements" 
      const results = [];
      const actualData = guestAnalysisData?.guestAnalysis || guestAnalysisData;
      
      if (actualData?.Contractor_1_Information?.Risk_Alerts) {
        results.push({
          name: 'Contractor 1',
          missing: actualData.Contractor_1_Information.Risk_Alerts.length,
          items: actualData.Contractor_1_Information.Risk_Alerts.map(alert => alert.title),
          coverage: 'Basic Coverage'
        });
      }
      
      if (actualData?.Contractor_2_Information?.Risk_Alerts) {
        results.push({
          name: 'Contractor 2',
          missing: actualData.Contractor_2_Information.Risk_Alerts.length,
          items: actualData.Contractor_2_Information.Risk_Alerts.map(alert => alert.title),
          coverage: 'Basic Coverage'
        });
      }
      
      return results;
    }
    
    if (!evaluationData) return [];
    
    const results = [];
    
    // Dynamic evaluation - check all contractor properties
    Object.keys(evaluationData).forEach(key => {
      if (key.startsWith('contractor_') && evaluationData[key]) {
        const contractor = evaluationData[key];
        const missing = contractor.missing_information || [];
        results.push({
          name: contractor.contractor_name,
          missing: missing.length,
          items: missing,
          coverage: contractor.sow_coverage
        });
      }
    });
    
    return results;
  };

  // Prepare allowances data for chart
  const prepareAllowancesData = () => {
    if (!contractors.length) return [];
    
    // Get all allowance categories from all contractors
    const categories = new Set();
    contractors.forEach(contractor => {
      if (contractor.allowances?.direct_costs) {
        Object.keys(contractor.allowances.direct_costs).forEach(key => {
          categories.add(key);
        });
      }
    });

    // Create data for charts - use all categories
    return Array.from(categories).map(category => {
      const dataPoint = { name: category.replace(/_/g, ' ') };
      
      contractors.forEach(contractor => {
        const contractorName = contractor.contractor.contractor_name;
        const value = contractor.allowances?.direct_costs?.[category] || 0;
        const cleanValue = typeof value === 'string' ? parseFloat(value.replace(/[\$,]/g, '')) : value;
        dataPoint[contractorName] = cleanValue || 0;
      });
      
      return dataPoint;
    });
  };

  // Calculate major cost differences - only if data is available
  const calculateCostBreakdown = () => {
    // Return empty array since we don't have actual breakdown data
    // This should be populated from JSON if breakdown data exists
    return [];
  };

  const costData = prepareCostData();
  const outliers = detectOutliers();
  const missingElements = findMissingElements();
  const allowancesData = prepareAllowancesData();
  const costBreakdown = calculateCostBreakdown();
  const alignmentScores = getAlignmentScores();

  // Calculate cost difference - works with any number of contractors
  const costRange = costData.length > 0 ? {
    min: Math.min(...costData.map(d => d.cost)),
    max: Math.max(...costData.map(d => d.cost))
  } : { min: 0, max: 0 };
  const costDifference = costRange.max - costRange.min;
  const averagePrice = costData.length > 0 ? 
    costData.reduce((sum, item) => sum + item.cost, 0) / costData.length : 0;

  return (
    <div className={styles.overviewContainer}>
      <div className={styles.overviewHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerText}>
            <h2>Your Proposal Analysis</h2>
            <p>We've analyzed your contractor proposals and found some interesting insights that could save you thousands.</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button 
            className={styles.downloadPDFButton}
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
            title={`Download comprehensive PDF report (${PDFService.getEstimatedFileSize({
              recommendationData,
              evaluationData,
              consolidatedData,
              guestAnalysisData,
              userPlan,
              hasActiveSubscription
            })})`}
          >
            <i className={isGeneratingPDF ? "fas fa-spinner fa-spin" : "fas fa-file-pdf"}></i>
            <span>{isGeneratingPDF ? 'Generating...' : 'Download PDF Report'}</span>
          </button>
        </div>
        {isUsingGuestData && (
          <div className={styles.dataSourceNotice}>
            <i className="fas fa-info-circle"></i>
            <span>
              {userPlan === 'free' 
                ? 'Showing basic analysis available on the free plan. Upgrade for comprehensive detailed analysis.'
                : 'Showing basic analysis. Upgrade for comprehensive detailed analysis.'
              }
            </span>
          </div>
        )}
      </div>

      {/* Recommendation Section */}
      {(recommendationData || (isUsingGuestData && (guestAnalysisData?.guestAnalysis || guestAnalysisData)?.comparision?.Recommendation)) && (
        <div className={styles.recommendationSection}>
          <div className={styles.recommendationCard}>
            <div className={styles.recommendationHeader}>
              <i className="fas fa-award"></i>
              <h3>Recommended Contractor</h3>
            </div>
            <div className={styles.recommendationContent}>
              <div className={styles.recommendedName}>
                {isUsingGuestData 
                  ? (guestAnalysisData?.guestAnalysis || guestAnalysisData)?.comparision?.Recommendation?.Preferred_Contractor
                  : recommendationData.recommended_contractor
                }
              </div>
              {canAccessRecommendationReasoning() ? (
                <p className={styles.recommendationText}>
                  {isUsingGuestData 
                    ? (guestAnalysisData?.guestAnalysis || guestAnalysisData)?.comparision?.Recommendation?.Reason
                    : (recommendationData.reasoning || "Based on comprehensive analysis of cost, quality, timeline, and alignment with your requirements.")
                  }
                </p>
              ) : (
                <>
                  <p className={styles.recommendationText}>
                    {isUsingGuestData 
                      ? "You have basic recommendation access. Upgrade for detailed reasoning and advanced analysis."
                      : "Unlock full analysis to see detailed reasoning"
                    }
                  </p>
                  <button 
                    className={styles.seeWhyButton}
                    onClick={onUpgradeClick}
                  >
                    <i className="fas fa-unlock"></i> See Why
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={styles.analysisGrid}>
        {/* Cost Comparison Card */}
        <div className={styles.analysisCard}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <i className="fas fa-chart-bar"></i>
            </div>
            <h3>Cost Comparison</h3>
          </div>
          
          <div className={styles.chartContainer}>
            <ResponsiveContainer width="100%" height={contractors.length > 3 ? 220 : 200}>
              <BarChart 
                data={costData} 
                margin={{ 
                  top: 20, 
                  right: 30, 
                  left: 20, 
                  bottom: contractors.length > 3 ? 15 : 5 
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e3f2fd" />
                <XAxis 
                  dataKey="name" 
                  fontSize={contractors.length > 3 ? 10 : 12}
                  interval={0}
                  angle={0}
                  textAnchor="middle"
                  height={contractors.length > 3 ? 40 : 30}
                />
                <YAxis tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`} fontSize={12} />
                <Tooltip 
                  formatter={(value, name, props) => [`$${value.toLocaleString()}`, 'Cost']}
                  labelFormatter={(label, payload) => {
                    if (payload && payload[0] && payload[0].payload.fullName) {
                      return payload[0].payload.fullName;
                    }
                    return label;
                  }}
                />
                <Bar 
                  dataKey="cost" 
                  radius={[4, 4, 0, 0]} 
                  maxBarSize={contractors.length > 4 ? 60 : 80}
                >
                  {costData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={contractorColors[entry.name]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {costBreakdown.length > 0 && (
            <div className={styles.costBreakdown}>
              <h4>Major Cost Differences:</h4>
              {costBreakdown.map((item, index) => (
                <div key={index} className={styles.breakdownItem}>
                  <div className={styles.breakdownLabel}>{item.label}:</div>
                  <div className={styles.breakdownBar}>
                    <div 
                      className={styles.breakdownFill} 
                      style={{
                        width: `${item.percentage}%`, 
                        backgroundColor: item.color
                      }}
                    ></div>
                  </div>
                  <div className={styles.breakdownValue}>+{item.percentage}%</div>
                </div>
              ))}
            </div>
          )}
          
          <div className={styles.cardInsight}>
            <span className={styles.insightAmount}>
              ${costDifference.toLocaleString()} difference
            </span> between {contractors.length > 2 ? 'highest and lowest' : ''} proposals. 
            Costs range from <strong>${costRange.min.toLocaleString()}</strong> to <strong>${costRange.max.toLocaleString()}</strong>
            {contractors.length > 2 && <span> across {contractors.length} contractors</span>}
            <br />
            <br />
            <span>Average price: <strong>${averagePrice.toLocaleString()}</strong></span>
          </div>
        </div>

        {/* Price Comparison Card */}
        <div className={styles.analysisCard}>
          <div className={`${styles.cardHeader} ${styles.cardHeaderWithDropdown}`}>
            <div className={styles.cardHeaderLeft}>
              <div className={styles.cardIcon}>
                <i className="fas fa-chart-line"></i>
              </div>
              <h3>Price Comparison</h3>
            </div>
            <div className={styles.comparisonDropdown}>
              <select 
                value={comparisonMode} 
                onChange={(e) => setComparisonMode(e.target.value)}
                className={styles.dropdownSelect}
              >
                <option value="average">vs Average</option>
                <option value="highest">vs Highest</option>
                <option value="lowest">vs Lowest</option>
              </select>
              <i className="fas fa-chevron-down"></i>
            </div>
          </div>
          
          <div className={styles.outliersContent}>
            {outliers.map((item, index) => (
              <div key={index} className={styles.outlierItem}>
                <div className={styles.outlierInfo}>
                  <div className={styles.outlierName}>{item.name}</div>
                  <div className={styles.outlierPrice}>${item.cost.toLocaleString()}</div>
                </div>
                <div className={item.isHigher ? styles.outlierHigh : styles.outlierLow}>
                  {item.isHigher ? '+' : ''}{item.percentageFromRef}%
                </div>
              </div>
            ))}

            <div className={styles.outlierExplanation}>
              <h4>What this means:</h4>
              <ul>
                <li>Percentages show how much higher (+) or lower (-) each contractor is vs {comparisonMode}</li>
                <li>A higher price may include more complete scope or premium materials</li>
                <li>A lower price may indicate competitive pricing or missing elements</li>
                <li>Reference ({comparisonMode}): <strong>${outliers[0]?.referencePrice?.toLocaleString()}</strong></li>
              </ul>
            </div>
          </div>
          
          {/* <div className={styles.cardInsight}>
            {outliers.some(o => o.isOutlier) 
              ? <span>Some proposals are <span className={styles.alertText}>significantly different</span> from the {comparisonMode} price.</span>
              : `All proposals are close to the ${comparisonMode} price range.`}
          </div> */}
        </div>

        {/* Missing Elements Card */}
        <div className={styles.analysisCard}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <i className="fas fa-clipboard-check"></i>
            </div>
            <h3>
              Missing Elements
              <div className={styles.tooltipContainer}>
                <i className="fas fa-info-circle"></i>
                <div className={styles.tooltip}>
                  <strong>Missing Elements</strong> are important work items or details that are not clearly specified in a contractor's proposal. These could include permits, specific materials, cleanup, or safety measures that might become unexpected costs later.
                </div>
              </div>
            </h3>
          </div>
          
          {canAccessDetailedAnalysis() ? (
            <div className={styles.missingContent}>
              {missingElements.map((item, index) => (
                <div key={index} className={styles.missingItem}>
                  <div className={styles.missingInfo}>
                    <div className={styles.missingName}>{item.name}</div>
                    <div className={styles.missingCoverage}>{item.coverage} coverage</div>
                  </div>
                  <div className={styles.missingCount}>
                    {item.missing > 0 
                      ? <span className={styles.missingBadge}>{item.missing} missing</span>
                      : <span className={styles.completeBadge}>Complete</span>
                    }
                  </div>
                </div>
              ))}
              
              {missingElements.some(item => item.items && item.items.length > 0) && (
                <div className={styles.missingDetails}>
                  <h4>Missing elements by contractor:</h4>
                  {missingElements.filter(item => item.items && item.items.length > 0).map((item, index) => {
                    const isExpanded = expandedMissingElements[item.name];
                    const itemsToShow = isExpanded ? item.items : item.items.slice(0, 2);
                    const hasMore = item.items.length > 2;
                    
                    return (
                      <div key={index} className={styles.expandableMissingSection}>
                        <div 
                          className={styles.missingContractorHeader}
                          onClick={() => toggleMissingElements(item.name)}
                        >
                          <div className={styles.contractorHeaderLeft}>
                            <strong>{item.name}:</strong>
                            {!isExpanded && (
                              <span className={styles.missingItemsList}>
                                {itemsToShow.join(', ')}
                                {hasMore && (
                                  <span className={styles.moreItemsIndicator}>
                                    {' '}and {item.items.length - 2} more...
                                  </span>
                                )}
                              </span>
                            )}
                            {isExpanded && (
                              <span className={styles.expandedHeaderText}>
                                Click to collapse and see summary
                              </span>
                            )}
                          </div>
                          {hasMore && (
                            <div className={styles.expandToggle}>
                              <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                            </div>
                          )}
                        </div>
                        
                        {isExpanded && hasMore && (
                          <div className={styles.expandedMissingItems}>
                            <div className={styles.allMissingItems}>
                              <strong>All missing elements:</strong>
                              <ul>
                                {item.items.map((missingItem, idx) => (
                                  <li key={idx}>{missingItem}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  <div className={styles.missingWarning}>
                    <i className="fas fa-exclamation-circle"></i>
                    <span>Missing elements can increase final costs by 10-25%</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.lockedContent}>
              <div className={styles.lockIcon}>
                <i className="fas fa-shield-alt"></i>
              </div>
              <p>Missing elements analysis reveals which contractors left out key project components that could increase your final costs by 10-25%.</p>
              <button className={styles.upgradeButton} onClick={onUpgradeClick}>
                <i className="fas fa-crown"></i> Unlock Missing Elements Analysis (Complete Plan)
              </button>
            </div>
          )}
          
          <div className={styles.cardInsight}>
            {canAccessDetailedAnalysis() ? (
              missingElements.some(m => m.missing > 0)
                ? <span>Some proposals are <span className={styles.alertText}>missing key elements</span> that may affect your project.</span>
                : "All proposals include all standard elements."
            ) : (
              <span>
                {userPlan === 'free' 
                  ? 'Upgrade to Complete plan to see detailed missing elements analysis that could save you 10-25% on unexpected costs.'
                  : userPlan === 'essential'
                  ? 'Upgrade to Complete plan to see detailed missing elements analysis.'
                  : 'Upgrade to Complete or Expert plan to see detailed missing elements analysis.'
                }
              </span>
            )}
          </div>
        </div>

        {/* Allowances Breakdown Card */}
        <div className={styles.analysisCard}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <i className="fas fa-dollar-sign"></i>
            </div>
            <h3>Allowances Breakdown</h3>
          </div>
          
          {/* Contractor Totals Section */}
          {canAccessDetailedAnalysis() ? (
            <div className={styles.allowanceSummary}>
              {contractors.map((contractor, index) => {
                const name = contractor.contractor.contractor_name;
                const allowanceKeys = Object.keys(contractor.allowances?.direct_costs || {});
                const total = Object.values(contractor.allowances?.direct_costs || {}).reduce((sum, val) => {
                  const numVal = typeof val === 'string' ? parseFloat(val.replace(/[\$,]/g, '')) : val;
                  return sum + (numVal || 0);
                }, 0);
                
                return (
                  <div key={index} className={styles.allowanceContractor}>
                    <div className={styles.contractorHeader}>
                      <div 
                        className={styles.contractorBadge}
                        style={{ backgroundColor: contractorColors[name] }}
                      >
                        {name.split(' ').map(word => word[0]).join('').toUpperCase()}
                      </div>
                      <div className={styles.contractorInfo}>
                        <div className={styles.contractorName}>{name}</div>
                        <div className={styles.contractorItems}>{allowanceKeys.length} allowance items</div>
                      </div>
                    </div>
                    <div className={styles.contractorTotal}>
                      <div className={styles.totalLabel}>Total Allowances</div>
                      <div className={styles.totalAmount}>${total.toLocaleString()}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.lockedContent}>
              <div className={styles.lockIcon}>
                <i className="fas fa-chart-pie"></i>
              </div>
              <p>Detailed allowances breakdown reveals exactly where your money goes and identifies potential cost-saving opportunities across contractors.</p>
              <button className={styles.upgradeButton} onClick={onUpgradeClick}>
                <i className="fas fa-crown"></i> Unlock Detailed Allowances Breakdown (Complete Plan)
              </button>
            </div>
          )}

          {/* Top Categories Chart */}
          {canAccessDetailedAnalysis() && (
            <div className={styles.topCategoriesSection}>
              <h4>Top Allowance Categories</h4>
              
              {/* Chart View Toggle */}
              <div className={styles.chartViewToggle}>
                <button 
                  className={`${styles.toggleButton} ${allowancesViewMode === 'chart' ? styles.active : ''}`}
                  onClick={() => setAllowancesViewMode('chart')}
                >
                  <i className="fas fa-chart-bar"></i> Chart View
                </button>
                <button 
                  className={`${styles.toggleButton} ${allowancesViewMode === 'table' ? styles.active : ''}`}
                  onClick={() => setAllowancesViewMode('table')}
                >
                  <i className="fas fa-table"></i> Table View
                </button>
              </div>

              {allowancesViewMode === 'chart' ? (
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart 
                      data={allowancesData.slice(0, 4)} 
                      margin={{ top: 10, right: 30, left: 20, bottom: 75 }}
                      barCategoryGap="15%"
                      barGap={2}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="name" 
                        fontSize={10}
                        angle={-45}
                        textAnchor="end"
                        height={40}
                        interval={0}
                        tickFormatter={(value) => {
                          // Truncate long category names more aggressively
                          const maxLength = contractors.length > 2 ? 8 : 10;
                          return value.length > maxLength ? value.substring(0, maxLength - 2) + '..' : value;
                        }}
                      />
                      <YAxis 
                        tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`} 
                        fontSize={11} 
                      />
                      <Tooltip 
                        formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
                        labelFormatter={(label) => {
                          // Show full category name in tooltip
                          const fullCategory = allowancesData.find(item => 
                            item.name.toLowerCase().startsWith(label.toLowerCase().replace('..', ''))
                          );
                          return fullCategory ? fullCategory.name : label;
                        }}
                        labelStyle={{ color: '#374151', fontWeight: '500' }}
                        contentStyle={{ 
                          background: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                      <Legend 
                        verticalAlign="bottom" 
                        height={30}
                        wrapperStyle={{ paddingTop: '10px' }}
                        formatter={(value) => {
                          // Truncate legend names
                          const maxLength = contractors.length > 3 ? 10 : 15;
                          return value.length > maxLength ? value.substring(0, maxLength - 3) + '...' : value;
                        }}
                      />
                      {contractors.map((contractor, index) => {
                        const name = contractor.contractor.contractor_name;
                        // Adjust legend name length based on number of contractors
                        let legendMaxLength;
                        if (contractors.length <= 2) {
                          legendMaxLength = 18;
                        } else if (contractors.length <= 3) {
                          legendMaxLength = 12;
                        } else {
                          legendMaxLength = 10;
                        }
                        
                        return (
                          <Bar 
                            key={name} 
                            dataKey={name} 
                            fill={contractorColors[name]}
                            radius={[2, 2, 0, 0]}
                            name={name.length > legendMaxLength ? name.substring(0, legendMaxLength - 3) + '...' : name}
                            maxBarSize={contractors.length > 3 ? 30 : 40}
                          />
                        );
                      })}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                /* Table View */
                <div className={styles.allowancesTable}>
                  {allowancesData.slice(0, 8).map((category, index) => (
                    <div key={index} className={styles.compactRow}>
                      <div className={styles.compactCategory}>
                        <span className={styles.categoryName}>{category.name}</span>
                      </div>
                      <div className={styles.compactValues}>
                        {contractors.map((contractor, contractorIndex) => {
                          const value = category[contractor.contractor.contractor_name] || 0;
                          return (
                            <div key={contractorIndex} className={styles.compactValue}>
                              <div 
                                className={styles.valueIndicator}
                                style={{ backgroundColor: contractorColors[contractor.contractor.contractor_name] }}
                              ></div>
                              <span className={styles.valueText}>
                                {value > 0 ? `$${value.toLocaleString()}` : '-'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  
                  {allowancesData.length > 8 && (
                    <div className={styles.tableFooter}>
                      <span>Showing top 8 categories • {contractors.length} contractors</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className={styles.cardInsight}>
            {canAccessDetailedAnalysis() ? (
              <>
                <span className={styles.insightAmount}>
                  ${(() => {
                    if (contractors.length >= 2) {
                      const totals = contractors.map(contractor => {
                        return Object.values(contractor.allowances?.direct_costs || {}).reduce((sum, val) => {
                          const numVal = typeof val === 'string' ? parseFloat(val.replace(/[\$,]/g, '')) : val;
                          return sum + (numVal || 0);
                        }, 0);
                      });
                      const difference = Math.abs(totals[0] - totals[1]);
                      const percentage = ((difference / Math.min(...totals)) * 100).toFixed(0);
                      return `${difference.toLocaleString()} (${percentage}% difference)`;
                    }
                    return '0';
                  })()}
                </span> in total allowances between contractors.
              </>
            ) : (
              <span>
                {userPlan === 'free' 
                  ? 'Upgrade to Complete plan to see detailed allowances breakdown and identify cost-saving opportunities.'
                  : userPlan === 'essential'
                  ? 'Upgrade to Complete plan to see detailed allowances breakdown and comparisons.'
                  : 'Upgrade to Complete or Expert plan to see detailed allowances analysis and comparisons.'
                }
              </span>
            )}
          </div>
        </div>

        {/* Contractor Evaluation Scores Card - Available for Essential+ users */}
        <div className={styles.analysisCard}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <i className="fas fa-star"></i>
            </div>
            <h3>Contractor Evaluation Scores</h3>
            <div className={styles.infoTooltip}>
              <i className="fas fa-info-circle"></i>
              <div className={styles.tooltipContent}>
                These scores are generated by AI analysis of each contractor's proposal documents. We evaluate their communication clarity, safety measures, project approach, and professional behavior to help you assess contractor quality and reliability.
              </div>
            </div>
          </div>
          
          {canAccessContractorScoring() ? (
            <div className={styles.evaluationContent}>
              {contractors.map((contractor, index) => {
                const contractorName = contractor.contractor.contractor_name;
                
                if (isUsingGuestData) {
                  // Use guest data format
                  const actualData = guestAnalysisData?.guestAnalysis || guestAnalysisData;
                  const contractorKey = index === 0 ? 'Contractor_1_Score' : 'Contractor_2_Score';
                  const contractorScore = actualData?.comparision?.[contractorKey];
                  
                  if (!contractorScore) return null;
                  
                  const overallScore = Math.round((contractorScore.Total / 10) * 10) || 0; // Convert to 0-10 scale
                  
                  // Get recommendation data for alignment score
                  const transformedData = transformGuestData();
                  const recommendationItem = transformedData.recommendation?.ranking?.find(
                    item => item.contractor_name === contractorName
                  );
                  
                  return (
                    <div key={index} className={styles.evaluationContractor}>
                      <div className={styles.evaluationHeader}>
                        <div className={styles.evaluationInfo}>
                          <div 
                            className={styles.evaluationAvatar} 
                            style={{backgroundColor: contractorColors[contractorName]}}
                          >
                            {contractorName.split(' ').map(word => word[0]).join('').toUpperCase()}
                          </div>
                          <div>
                            <h4>{contractorName}</h4>
                            <div className={styles.overallScore}>
                              Quality Score: <span className={styles.scoreValue}>{overallScore}/10</span>
                            </div>
                          </div>
                        </div>
                        <div className={styles.alignmentScore}>
                          <div className={styles.alignmentLabel}>Overall Score</div>
                          <div className={styles.alignmentValue}>
                            {contractorScore.Total}/100
                          </div>
                        </div>
                      </div>
                      
                      <div className={styles.categoryScores}>
                        <h5>Category Breakdown:</h5>
                        <div className={styles.scoreGrid}>
                          {Object.entries(contractorScore.Breakdown || {}).slice(0, 4).map(([category, score]) => (
                            <div key={category} className={styles.categoryScore}>
                              <span className={styles.categoryLabel}>{category}</span>
                              <span className={`${styles.categoryValue} ${score >= 12 ? styles.highScore : score >= 8 ? styles.mediumScore : styles.lowScore}`}>
                                {score}/15
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                
                // Regular evaluation data logic
                const evaluationKey = `contractor_${index + 1}`;
                const contractorEval = evaluationData?.[evaluationKey];
                
                console.log('Debug - Evaluation data:', {
                  contractorName,
                  evaluationKey,
                  contractorEval,
                  evaluationData
                });
                
                if (!contractorEval) return null;
                
                // Calculate overall score from all categories
                const categories = [
                  'communication_and_accountability',
                  'documentation_and_deliverables', 
                  'logistics_and_execution_plan',
                  'scope_and_methodology',
                  'safety_measurements',
                  'labor_and_workforce',
                  'contracting_behavior'
                ];
                
                const validScores = categories
                  .map(cat => contractorEval[cat]?.score)
                  .filter(score => score !== undefined && score !== null);
                
                const overallScore = validScores.length > 0 ? 
                  Math.round(validScores.reduce((sum, score) => sum + score, 0) / validScores.length) : 0;
                
                console.log('Debug - Score calculation:', {
                  contractorName,
                  categories,
                  validScores,
                  overallScore
                });
                
                // Get recommendation data for this contractor
                // Need to match contractor names more flexibly since they might differ slightly
                const recommendationItem = recommendationData?.ranking?.find(
                  item => {
                    const recName = item.contractor_name.toLowerCase();
                    const consolidatedName = contractorName.toLowerCase();
                    // Check if names match exactly or if one contains the other
                    return recName === consolidatedName || 
                           recName.includes(consolidatedName.split(' ')[0]) ||
                           consolidatedName.includes(recName.split(' ')[0]);
                  }
                );
                
                console.log('Debug - Contractor matching:', {
                  contractorName,
                  recommendationData: recommendationData?.ranking,
                  foundMatch: recommendationItem,
                  alignmentScore: recommendationItem?.alignment_score
                });
                
                return (
                  <div key={index} className={styles.evaluationContractor}>
                    <div className={styles.evaluationHeader}>
                      <div className={styles.evaluationInfo}>
                        <div 
                          className={styles.evaluationAvatar} 
                          style={{backgroundColor: contractorColors[contractorName]}}
                        >
                          {contractorName.split(' ').map(word => word[0]).join('').toUpperCase()}
                        </div>
                                                  <div>
                            <h4>{contractorName}</h4>
                            <div className={styles.overallScore}>
                              Quality Score: <span className={styles.scoreValue}>{overallScore}/10</span>
                            </div>
                          </div>
                      </div>
                      <div className={styles.alignmentScore}>
                        <div className={styles.alignmentLabel}>Project Alignment</div>
                        <div className={styles.alignmentValue}>
                          {recommendationItem?.alignment_score !== undefined ? `${recommendationItem.alignment_score}%` : 'N/A'}
                        </div>
                      </div>
                    </div>
                    

                    
                    <div className={styles.categoryScores}>
                      <h5>Category Breakdown:</h5>
                      <div className={styles.scoreGrid}>
                        {[
                          { key: 'communication_and_accountability', label: 'Communication' },
                          { key: 'safety_measurements', label: 'Safety' },
                          { key: 'contracting_behavior', label: 'Behavior' },
                          { key: 'scope_and_methodology', label: 'Scope' }
                        ].map(({ key, label }) => {
                          const score = contractorEval[key]?.score || 0;
                          return (
                            <div key={key} className={styles.categoryScore}>
                              <span className={styles.categoryLabel}>{label}</span>
                              <span className={`${styles.categoryValue} ${score >= 8 ? styles.highScore : score >= 5 ? styles.mediumScore : styles.lowScore}`}>
                                {score}/10
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.lockedContent}>
              <div className={styles.lockIcon}>
                <i className="fas fa-trophy"></i>
              </div>
              <p>Detailed contractor evaluation reveals scoring across 10+ categories including safety, communication, and reliability to help you choose the right contractor.</p>
              <button className={styles.upgradeButton} onClick={onUpgradeClick}>
                <i className="fas fa-crown"></i> Unlock Contractor Scores (Essential Plan)
              </button>
            </div>
          )}
          
          <div className={styles.cardInsight}>
            {canAccessContractorScoring() ? (
              <div className={styles.expandableScoringSection}>
                <div 
                  className={styles.scoringToggleHeader}
                  onClick={() => setExpandedScoringExplanation(!expandedScoringExplanation)}
                >
                  <span className={styles.scoringToggleText}>Understanding These Scores</span>
                  <i className={`fas fa-chevron-${expandedScoringExplanation ? 'up' : 'down'} ${styles.scoringToggleIcon}`}></i>
                </div>
                
                {expandedScoringExplanation && (
                  <div className={styles.scoringExplanationContent}>
                    <div style={{ marginBottom: '12px', lineHeight: '1.5' }}>
                      <div style={{ marginBottom: '6px' }}><strong>Quality Score:</strong> Overall contractor quality based on proposal analysis. 8-10: Excellent quality, 6-7: Good quality, 4-5: Average quality, Below 4: Poor quality with potential concerns.</div>
                      <div><strong>Project Alignment:</strong> How well the contractor fits your specific project needs and requirements.</div>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: '1.5' }}>
                      <div style={{ marginBottom: '4px' }}><strong>Communication:</strong> Clarity of written communication, responsiveness, and accountability measures in the proposal</div>
                      <div style={{ marginBottom: '4px' }}><strong>Safety:</strong> Safety protocols, insurance coverage, and risk management measures outlined in the proposal</div>
                      <div style={{ marginBottom: '4px' }}><strong>Behavior:</strong> Professional conduct, reliability indicators, and business practices demonstrated in the proposal</div>
                      <div><strong>Scope:</strong> Completeness of project scope, methodology clarity, and technical approach described in the proposal</div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <span>
                {userPlan === 'free' 
                  ? 'Upgrade to Essential plan to see detailed contractor evaluation scores and risk analysis.'
                  : 'Upgrade to see detailed contractor evaluation scores and risk analysis.'
                }
              </span>
            )}
          </div>
        </div>

        {/* Scope Comparison Card */}
        <div className={styles.analysisCard}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <i className="fas fa-tasks"></i>
            </div>
            <h3>Scope Comparison</h3>
          </div>
          
          <div className={styles.scopeContent}>
            {contractors.map((contractor, index) => {
              const name = contractor.contractor.contractor_name;
              
              // Parse price correctly
              let price = 0;
              if (contractor.payment_terms && contractor.payment_terms.total_cost) {
                const costStr = contractor.payment_terms.total_cost.toString();
                const cleanCost = costStr.replace(/[\$,]/g, '');
                price = parseFloat(cleanCost) || 0;
              }
              
              const scope = contractor.project_scope;
              const exclusions = contractor.exclusions || [];
              
              return (
                <div key={index} className={styles.scopeContractor}>
                  <div className={styles.scopeHeader}>
                    <div className={styles.scopeInfo}>
                      <div 
                        className={styles.scopeAvatar} 
                        style={{backgroundColor: contractorColors[name]}}
                      >
                        {name.split(' ').map(word => word[0]).join('').toUpperCase()}
                      </div>
                      <div>
                        <h4>{name}</h4>
                        <div className={styles.scopePrice}>${price.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                  
                  <p className={styles.scopeDescription}>
                    {scope.substring(0, 150)}...
                    {exclusions.length > 0 && (
                      <span className={styles.scopeExclusions}>
                        <br/><strong>Excludes:</strong> {exclusions[0]}
                        {exclusions.length > 1 && '...'}
                      </span>
                    )}
                  </p>
                  
                  {!canAccessDetailedScope() && (
                    <div className={styles.scopeUnlock}>
                      <i className="fas fa-lock"></i> 
                      <span>Unlock to see detailed scope breakdown</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          <div className={styles.cardInsight}>
            <span className={styles.insightHighlight}>Key differences:</span> Contractors vary in scope detail and approach.
            {contractors.length >= 2 && (
              <span> {contractors[0].contractor.contractor_name} vs {contractors[1].contractor.contractor_name} show different project methodologies.</span>
            )}
          </div>
        </div>
      </div>

      {/* Call to Action - Only show for users who can upgrade */}
      {(userPlan === 'free' || (hasActiveSubscription && ['essential', 'complete'].includes(userPlan))) && (
        <div className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            {userPlan === 'free' ? (
              <>
                {isUsingGuestData ? (
                  <>
                    <h3>You're on the free plan - viewing basic analysis</h3>
                    <p>Upgrade to unlock detailed contractor evaluation, risk analysis, and comprehensive comparisons with our advanced AI analysis.</p>
                    <div className={styles.guestDataNotice}>
                      <i className="fas fa-info-circle"></i>
                      <span>Currently showing: Basic comparison from free plan</span>
                    </div>
                  </>
                ) : (
                  <>
                    <h3>Ready to make the right decision?</h3>
                    <p>Unlock contractor evaluation scores, risk analysis, and detailed comparisons to make the best choice for your project.</p>
                  </>
                )}
                
                <button 
                  className={styles.unlockButton} 
                  onClick={onUpgradeClick}
                >
                  <i className="fas fa-unlock"></i> Start with Essential Plan
                </button>
              </>
            ) : userPlan === 'essential' ? (
              <>
                <h3>Unlock deeper project insights</h3>
                <p>Upgrade to Complete plan for detailed allowances breakdown, missing elements analysis, and comprehensive contractor comparisons.</p>
                
                <button 
                  className={styles.unlockButton} 
                  onClick={onUpgradeClick}
                >
                  <i className="fas fa-arrow-up"></i> Upgrade to Complete
                </button>
              </>
            ) : userPlan === 'complete' ? (
              <>
                <h3>Get the ultimate analysis experience</h3>
                <p>Upgrade to Expert for advanced risk modeling, negotiation strategies, and premium project management tools.</p>
                
                <button 
                  className={styles.unlockButton} 
                  onClick={onUpgradeClick}
                >
                  <i className="fas fa-crown"></i> Upgrade to Expert
                </button>
              </>
            ) : null}
            
            <div className={styles.ctaBenefit}>
              <i className="fas fa-piggy-bank"></i>
              <span>Potentially save thousands with the right choice</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonOverview; 